import React, { useEffect, useMemo, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../technician-panel/utils/technicianPanelGoogleMaps';
import { fetchAgendaMapsKey } from '../services/scheduleService';
import OpenOsV2KmSlider from './OpenOsV2KmSlider';
import {
  buildRouteHelpPinEl,
  buildAgendaMonitoredTechPinEl,
  buildOriginPinEl,
  bringRouteHelpPinToFront,
  bringOriginPinToFront,
  clearRouteHelpPinFront,
  clearOriginPinFront,
  clusterRouteHelpPoints,
  createPinOverlay,
  isClientPointOnEl,
  osServiceBadge,
  ROUTE_PIN_Z,
} from './openOsV2RouteHelpPins';

function pointsFingerprint(points) {
  return (points || [])
    .map((p) => `${p.os}|${p.lat}|${p.lng}|${p.codTec}`)
    .join(';');
}

function redrawPinOverlays(list) {
  (list || []).forEach((ov) => {
    try {
      if (ov && typeof ov.draw === 'function') ov.draw();
    } catch (_) {
      /* ignore */
    }
  });
}

function groupOrdersByClient(orders) {
  const map = {};
  (orders || []).forEach((o) => {
    const cliente = String((o && o.cliente) || '').trim() || '—';
    if (!map[cliente]) map[cliente] = [];
    if (o && o.os) map[cliente].push(o);
  });
  return Object.keys(map).map((cliente) => ({ cliente, orders: map[cliente] }));
}

export default function OpenOsV2AgendaRouteMap({
  km,
  onKmChange,
  origin,
  originAddress,
  points,
  colorMap,
  loading,
  error,
  dayLabel,
  monitoredTechs,
}) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const circleRef = useRef(null);
  const originOverlayRef = useRef(null);
  const originInfoRef = useRef(null);
  const osOverlaysRef = useRef([]);
  const techOverlaysRef = useRef([]);
  const clickListenerRef = useRef(null);
  const fitKeyRef = useRef('');
  const colorMapRef = useRef(colorMap);
  const idleListenerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [openPin, setOpenPin] = useState(null);
  const pointsKey = useMemo(() => pointsFingerprint(points), [points]);
  const monitoredKey = useMemo(
    () =>
      (monitoredTechs || [])
        .map((t) => `${t.id}|${t.lat}|${t.lng}|${t.status}|${t.speedKmh}`)
        .join(';'),
    [monitoredTechs]
  );

  colorMapRef.current = colorMap;

  const clearOsPinFront = () => {
    osOverlaysRef.current.forEach((ov) => clearRouteHelpPinFront(ov && ov.el));
  };

  const bringOriginFront = () => {
    clearOsPinFront();
    const originEl = originOverlayRef.current && originOverlayRef.current.el;
    bringOriginPinToFront(originEl);
    setOpenPin(null);
  };
  const bringOriginFrontRef = useRef(bringOriginFront);
  bringOriginFrontRef.current = bringOriginFront;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const key = await fetchAgendaMapsKey();
        if (cancelled) return;
        if (!key) {
          setMapError('Mapa indisponível.');
          return;
        }
        const maps = await loadGoogleMaps(key);
        if (cancelled || !hostRef.current) return;
        const map = new maps.Map(hostRef.current, {
          center: { lat: -14.235, lng: -51.9253 },
          zoom: 5,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        });
        mapRef.current = map;
        clickListenerRef.current = map.addListener('click', () => {
          clearOsPinFront();
          clearOriginPinFront(originOverlayRef.current && originOverlayRef.current.el);
          setOpenPin(null);
        });
        setMapReady(true);
      } catch {
        if (!cancelled) setMapError('Falha ao carregar o mapa.');
      }
    })();
    return () => {
      cancelled = true;
      osOverlaysRef.current.forEach((ov) => ov.setMap(null));
      osOverlaysRef.current = [];
      techOverlaysRef.current.forEach((ov) => ov.setMap(null));
      techOverlaysRef.current = [];
      if (originInfoRef.current) originInfoRef.current.close();
      if (originOverlayRef.current) originOverlayRef.current.setMap(null);
      originOverlayRef.current = null;
      if (circleRef.current) circleRef.current.setMap(null);
      if (idleListenerRef.current && window.google && window.google.maps) {
        window.google.maps.event.removeListener(idleListenerRef.current);
        idleListenerRef.current = null;
      }
      if (clickListenerRef.current && window.google && window.google.maps) {
        window.google.maps.event.removeListener(clickListenerRef.current);
      }
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const maps = typeof window !== 'undefined' ? window.google && window.google.maps : null;
    if (!mapReady || !map || !maps || !origin) return;
    const addressTip = String(originAddress || '').trim() || 'Atendimento a abrir';
    const showOriginInfo = () => {
      if (!originInfoRef.current) return;
      originInfoRef.current.setContent(
        '<div style="font:600 12px/1.4 Plus Jakarta Sans,system-ui,sans-serif;max-width:260px;color:#333">' +
        addressTip.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])) +
        '</div>'
      );
      originInfoRef.current.open({ map, position: origin });
    };
    const hideOriginInfo = () => {
      if (originInfoRef.current) originInfoRef.current.close();
    };

    if (!originOverlayRef.current) {
      const el = buildOriginPinEl(addressTip);
      const overlay = createPinOverlay(maps, {
        lat: origin.lat,
        lng: origin.lng,
        el,
        zIndex: ROUTE_PIN_Z.origin,
        onClick: () => bringOriginFrontRef.current(),
      });
      el.addEventListener('pointerenter', showOriginInfo);
      el.addEventListener('pointerleave', hideOriginInfo);
      overlay._originHover = { showOriginInfo, hideOriginInfo };
      originOverlayRef.current = overlay;
      originInfoRef.current = new maps.InfoWindow({ disableAutoPan: true });
      overlay.setMap(map);
    } else {
      const ov = originOverlayRef.current;
      // Só reposiciona — setMap de novo pode disparar onRemove/onAdd e quebrar o pin.
      ov.setPosition(origin.lat, origin.lng);
      if (ov.el) {
        ov.el.title = addressTip;
        ov.el.setAttribute('aria-label', addressTip);
        if (ov._originHover) {
          ov.el.removeEventListener('pointerenter', ov._originHover.showOriginInfo);
          ov.el.removeEventListener('pointerleave', ov._originHover.hideOriginInfo);
        }
        ov._originHover = { showOriginInfo, hideOriginInfo };
        ov.el.addEventListener('pointerenter', showOriginInfo);
        ov.el.addEventListener('pointerleave', hideOriginInfo);
      }
    }

    const radiusM = Math.max(3, Number(km) || 5) * 1000;
    if (!circleRef.current) {
      circleRef.current = new maps.Circle({
        map,
        center: origin,
        radius: radiusM,
        strokeColor: '#6d3ef5',
        strokeOpacity: 0.7,
        strokeWeight: 1.5,
        fillColor: '#6d3ef5',
        fillOpacity: 0.08,
        clickable: false,
        zIndex: 1,
      });
    } else {
      circleRef.current.setMap(map);
      circleRef.current.setCenter(origin);
      circleRef.current.setRadius(radiusM);
    }

    const placeOsPins = () => {
      const prev = osOverlaysRef.current.slice();
      osOverlaysRef.current = [];
      prev.forEach((ov) => {
        try {
          if (ov) ov.setMap(null);
        } catch (_) {
          /* ignore */
        }
      });
      const clusters = clusterRouteHelpPoints(points, colorMapRef.current);
      const next = clusters.map((cluster) => {
        const el = buildRouteHelpPinEl(cluster);
        const overlay = createPinOverlay(maps, {
          lat: cluster.lat,
          lng: cluster.lng,
          el,
          onClick: (e) => {
            const originEl = originOverlayRef.current && originOverlayRef.current.el;
            if (e && originEl && isClientPointOnEl(e.clientX, e.clientY, originEl)) {
              bringOriginFrontRef.current();
              return;
            }
            osOverlaysRef.current.forEach((ov) => {
              if (ov && ov.el !== el) clearRouteHelpPinFront(ov.el);
            });
            clearOriginPinFront(originEl);
            bringRouteHelpPinToFront(el);
            setOpenPin({
              bairro: cluster.bairro,
              color: cluster.color,
              groups: groupOrdersByClient(cluster.orders),
              count: cluster.count,
            });
          },
        });
        overlay.setMap(map);
        return overlay;
      });
      osOverlaysRef.current = next;
      redrawPinOverlays(osOverlaysRef.current);
    };

    if (idleListenerRef.current) {
      maps.event.removeListener(idleListenerRef.current);
      idleListenerRef.current = null;
    }

    map.setCenter(origin);
    const fitKey = `${Number(origin.lat)}|${Number(origin.lng)}|${Number(km)}`;
    const bounds = circleRef.current.getBounds();
    maps.event.trigger(map, 'resize');

    // Uma única colocação de pins: idle só reposiciona (evita setMap(null)+onAdd com el nulo).
    if (fitKeyRef.current !== fitKey && bounds) {
      fitKeyRef.current = fitKey;
      idleListenerRef.current = maps.event.addListenerOnce(map, 'idle', () => {
        redrawPinOverlays(osOverlaysRef.current);
        redrawPinOverlays(techOverlaysRef.current);
        if (originOverlayRef.current && typeof originOverlayRef.current.draw === 'function') {
          originOverlayRef.current.draw();
        }
      });
      placeOsPins();
      window.requestAnimationFrame(() => {
        maps.event.trigger(map, 'resize');
        map.fitBounds(bounds, 28);
        map.setCenter(origin);
      });
    } else {
      idleListenerRef.current = maps.event.addListenerOnce(map, 'idle', () => {
        redrawPinOverlays(osOverlaysRef.current);
        redrawPinOverlays(techOverlaysRef.current);
        if (originOverlayRef.current && typeof originOverlayRef.current.draw === 'function') {
          originOverlayRef.current.draw();
        }
      });
      placeOsPins();
      maps.event.trigger(map, 'idle');
    }

    return () => {
      if (idleListenerRef.current) {
        maps.event.removeListener(idleListenerRef.current);
        idleListenerRef.current = null;
      }
    };
  }, [mapReady, origin, originAddress, pointsKey, km]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = typeof window !== 'undefined' ? window.google && window.google.maps : null;
    if (!mapReady || !map || !maps) return;

    const prevTechs = techOverlaysRef.current.slice();
    techOverlaysRef.current = [];
    prevTechs.forEach((ov) => {
      try {
        if (ov) ov.setMap(null);
      } catch (_) {
        /* ignore */
      }
    });
    techOverlaysRef.current = (monitoredTechs || []).map((tech) => {
      const lat = Number(tech.lat);
      const lng = Number(tech.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      const el = buildAgendaMonitoredTechPinEl(tech);
      const overlay = createPinOverlay(maps, { lat, lng, el });
      overlay.setMap(map);
      return overlay;
    }).filter(Boolean);
    redrawPinOverlays(techOverlaysRef.current);
    const once = maps.event.addListenerOnce(map, 'idle', () => {
      redrawPinOverlays(techOverlaysRef.current);
    });
    return () => {
      maps.event.removeListener(once);
    };
  }, [mapReady, monitoredKey]);

  useEffect(() => {
    clearOsPinFront();
    clearOriginPinFront(originOverlayRef.current && originOverlayRef.current.el);
    setOpenPin(null);
  }, [km, pointsKey]);

  const status = error || mapError;
  const osCount = (points || []).length;
  const monitoredCount = (monitoredTechs || []).length;

  return (
    <div className="oosv2-agenda-map-col">
      <div className="oosv2-agenda-map-head">
        <div className="oosv2-agenda-map-kicker">
          Veja serviços no raio perto deste atendimento
          {dayLabel ? ` · ${dayLabel}` : ''}
          {origin && !loading ? ` · ${osCount} OS` : ''}
          {monitoredCount ? ` · ${monitoredCount} técnico${monitoredCount === 1 ? '' : 's'} monitorado${monitoredCount === 1 ? '' : 's'}` : ''}
        </div>
        <OpenOsV2KmSlider value={km} onChange={onKmChange} />
      </div>
      <div className="oosv2-agenda-map-canvas-wrap">
        <div ref={hostRef} className="oosv2-agenda-map-canvas" />
        {loading && !origin ? (
          <div className="oosv2-agenda-map-cover">Buscando OS no raio…</div>
        ) : null}
        {!loading && status ? (
          <div className="oosv2-agenda-map-cover is-err">{status}</div>
        ) : null}
        {openPin ? (
          <div className="oosv2-agenda-pin-card">
            <button
              type="button"
              className="oosv2-agenda-pin-card-x"
              onClick={() => {
                clearOsPinFront();
                setOpenPin(null);
              }}
              aria-label="Fechar"
            >×</button>
            {(openPin.groups || []).map((g, i) => (
              <div key={i} className="oosv2-agenda-pin-card-block">
                <div className="oosv2-agenda-pin-card-client">{g.cliente}</div>
                <div className="oosv2-agenda-pin-card-os">
                  {(g.orders || []).map((order) => {
                    const badge = osServiceBadge(order);
                    return (
                      <span key={order.os} className="oosv2-agenda-pin-card-os-item">
                        <span
                          className={`service-type-badge-compact${badge.alert ? ' service-type-badge-compact--defeito-alert' : ''}`}
                          style={{ backgroundColor: badge.bg }}
                        >
                          {badge.text}
                        </span>
                        OS {order.os}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
